import type {
  Classroom,
  ClassSchedule,
  Attendance,
  GraduationChecklist,
  AttendanceAlert,
  ScheduleInput,
  ScheduleForAttendance,
  StudentForAttendance,
  AttendanceRecordInput,
  AttendanceStats
} from './controlEscolarTypes';

const API_URL = '/api';

export const getClassrooms = async (): Promise<Classroom[]> => {
  const response = await fetch(`${API_URL}/classrooms`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar aulas');
  return response.json();
};

export const createClassroom = async (data: Partial<Classroom>): Promise<Classroom> => {
  const response = await fetch(`${API_URL}/classrooms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Error al crear aula');
  return response.json();
};

export const updateClassroom = async (id: number, data: Partial<Classroom>): Promise<Classroom> => {
  const response = await fetch(`${API_URL}/classrooms/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Error al actualizar aula');
  return response.json();
};

export const getSchedules = async (): Promise<ClassSchedule[]> => {
  const response = await fetch(`${API_URL}/schedules`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar horarios');
  return response.json();
};

export const getSchedulesCalendarView = async (): Promise<any[]> => {
  const response = await fetch(`${API_URL}/schedules/calendar-view`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar vista de calendario');
  return response.json();
};

export const createSchedule = async (data: Partial<ClassSchedule>): Promise<ClassSchedule> => {
  const response = await fetch(`${API_URL}/schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    const error: any = new Error(errorData.error || 'Error al crear horario');
    error.response = { data: errorData };
    throw error;
  }
  return response.json();
};

export const recordAttendance = async (data: {
  alumnoId: number;
  classScheduleId: number;
  attendanceDate: string;
  isPresent: boolean;
  notes?: string;
}): Promise<Attendance> => {
  const response = await fetch(`${API_URL}/attendance`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Error al registrar asistencia');
  return response.json();
};

export const getAttendanceAlerts = async (): Promise<AttendanceAlert[]> => {
  const response = await fetch(`${API_URL}/attendance/dashboard-alerts`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar alertas de asistencia');
  return response.json();
};

// New attendance system services
export const getSchedulesForAttendance = async (periodId?: number): Promise<ScheduleForAttendance[]> => {
  const url = periodId 
    ? `${API_URL}/attendance/schedules?periodId=${periodId}`
    : `${API_URL}/attendance/schedules`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar horarios');
  return response.json();
};

export const getStudentsForSchedule = async (
  scheduleId: number,
  date?: string
): Promise<StudentForAttendance[]> => {
  const url = date
    ? `${API_URL}/attendance/schedules/${scheduleId}/students?date=${date}`
    : `${API_URL}/attendance/schedules/${scheduleId}/students`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar estudiantes');
  return response.json();
};

export const recordAttendanceBatch = async (
  data: AttendanceRecordInput
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_URL}/attendance/record`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Error al registrar asistencia');
  }
  return response.json();
};

export const getAttendanceStats = async (alumnoId: number): Promise<AttendanceStats> => {
  const response = await fetch(`${API_URL}/attendance/stats/${alumnoId}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar estadísticas de asistencia');
  return response.json();
};

export const getGraduationChecklist = async (alumnoId: number): Promise<GraduationChecklist> => {
  const response = await fetch(`${API_URL}/graduation/checklist/${alumnoId}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar checklist de graduación');
  return response.json();
};

export const updateGraduationChecklist = async (
  alumnoId: number,
  data: Partial<GraduationChecklist>
): Promise<GraduationChecklist> => {
  const response = await fetch(`${API_URL}/graduation/checklist/${alumnoId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Error al actualizar checklist');
  return response.json();
};

export const updateSchedule = async (id: number, data: ScheduleInput): Promise<any> => {
  const response = await fetch(`${API_URL}/schedules/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    const error: any = new Error(errorData.error || 'Error al actualizar horario');
    error.response = { data: errorData };
    throw error;
  }
  return response.json();
};

export interface ShiftScheduleResult {
  moves: { id: number; fromDate: string; toDate: string; toDia: string }[];
  warnings: string[];
  newPeriodEndDate: string | null;
}

// Recorre esta sesión y las siguientes de la misma materia una sesión hacia adelante.
export const shiftScheduleSeries = async (id: number): Promise<ShiftScheduleResult> => {
  const response = await fetch(`${API_URL}/schedules/${id}/shift`, {
    method: 'POST',
    credentials: 'include',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Error al recorrer la calendarización');
  return data;
};

export const deleteSchedule = async (id: number): Promise<void> => {
  const response = await fetch(`${API_URL}/schedules/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al eliminar horario');
  }
};
